import { invariantResponse } from '@epic-web/invariant'
import { type LoaderFunctionArgs } from '@remix-run/node'
import { useLoaderData, useNavigate } from '@remix-run/react'
import { ImagePlus, MessageCircle } from 'lucide-react'
import { CustomImage } from '#app/components/custom-image.js'
import { DialogUpscaler } from '#app/components/dialog-upscaler.js'
import { DialogueDetailer } from '#app/components/dialogue-detailer.js'
import { DialogueImage } from '#app/components/dialogue-image.js'
import { SelectionProvider } from '#app/components/image-selection-context.js'
import { Masonry } from '#app/components/masonry.js'
import { TraitBadge } from '#app/components/trait-badge.js'
import { Avatar, AvatarFallback } from '#app/components/ui/avatar'
import { Button } from '#app/components/ui/button.js'
import { ScrollArea } from '#app/components/ui/scroll-area'
import { Separator } from '#app/components/ui/separator'
import { getUserId } from '#app/utils/auth.server.js'
import { prisma } from '#app/utils/db.server.js'
import { decodeImageFormat, WORKFLOWS } from '#app/utils/helpers.js'
import { useImageDialogs } from '#app/utils/hooks/useImageDialogs.js'
import { useImageUrl } from '#app/utils/hooks/useImageUrl.js'
import { cn } from '#app/utils/misc.js'

export async function loader({ params, request }: LoaderFunctionArgs) {
	const slug = params.characterSlug
	invariantResponse(slug, 'characterSlug is required')

	const userId = await getUserId(request)

	const character = await prisma.character.findUnique({
		where: {
			slug,
		},
		include: {
			avatar: {
				select: {
					id: true,
					ownerId: true,
					format: true,
					createdAt: true,
					blurhash: true,
					nsfw: true,
				},
			},
		},
	})

	invariantResponse(character, 'Character not found')

	const generatedImagesCount = await prisma.generatedImage.count({
		where: {
			publicCharacters: {
				some: {
					id: character.id,
				},
			},
			OR: [
				userId ? { image: { ownerId: userId } } : { id: '-1' },
				{
					AND: [{ isPublic: true }, { image: { nsfw: false } }],
				},
			],
		},
	})

	return {
		character: {
			...character,
			avatar: character?.avatar
				? {
						id: character.avatar.id,
						ownerId: character.avatar.ownerId,
						url: `/resources/proxy/${character.avatar.ownerId}/${character.avatar.id}`,
						format: character.avatar.format,
						createdAt: character.avatar.createdAt,
						blurhash: character.avatar.blurhash,
						nsfw: character.avatar.nsfw,
						isPublic: true,
					}
				: null,
		},
		galleryCount: generatedImagesCount,
	}
}

export default function CharacterProfile() {
	const { character, galleryCount } = useLoaderData<typeof loader>()
	const { dialogState, openDialog, closeDialog } = useImageDialogs()
	const resp = useImageUrl(character.avatar)
	invariantResponse(character.avatar, 'Avatar is required')
	const navigate = useNavigate()

	//TODO: think about #634A7F
	return (
		<ScrollArea className="h-[calc(100vh-69px)] w-full bg-gradient-to-b from-background to-gray-100">
			<div>
				<header className="relative w-full bg-gradient-to-r from-primary to-[#634A7F] py-16 text-primary-foreground">
					<div className="bg-pattern absolute inset-0 opacity-10"></div>
					<div className="relative mx-auto flex max-w-5xl flex-col items-center justify-between px-4 md:flex-row md:px-8">
						<div className="flex flex-col items-center space-y-4 md:flex-row md:space-x-8 md:space-y-0">
							<Avatar className="h-40 w-40 rounded-full border-4 border-primary-foreground transition-transform duration-300 hover:scale-105">
								<CustomImage
									image={{
										...character.avatar,
										createdAt: new Date(character.avatar.createdAt),
										isPublic: true,
									}}
									width={768}
									actualImageUrl={resp.actualImageUrl}
									imageFormat={decodeImageFormat(character.avatar.format)}
									className="rounded-full object-cover"
								/>
								<AvatarFallback>AN</AvatarFallback>
							</Avatar>
							<div className="text-center md:text-left">
								<h1 className="mb-2 text-4xl font-bold tracking-tight">
									{character.name}
								</h1>
								<p className="text-primary-foreground/80 max-w-md text-lg font-light leading-relaxed">
									{character.description}
								</p>
							</div>
						</div>
						<div className="mt-6 flex space-x-4 md:mt-0">
							<Button
								variant="secondary"
								className="w-20"
								onClick={(e) => {
									e.preventDefault()
									e.stopPropagation()
									navigate(`/characters/${character.slug}/chat`)
								}}
							>
								<div className="flex flex-row items-center justify-center gap-1">
									<MessageCircle className="h-5 w-5" />
									Chat
								</div>
							</Button>
							<Button
								variant="secondary"
								disabled={!character.hasTrainedLora}
								className="w-28"
								onClick={(e) => {
									e.preventDefault()
									e.stopPropagation()
									navigate(`/generate/images?character=${character.slug}`)
								}}
							>
								<div className="flex flex-row items-center justify-center gap-1">
									<ImagePlus className="h-5 w-5" />
									Generate
								</div>
							</Button>
						</div>
					</div>
				</header>

				<main className="mx-auto max-w-5xl space-y-12 p-8">
					<section className="rounded-md p-2 transition-all duration-300">
						<h2 className="mb-4 text-2xl font-semibold text-card-foreground">
							Stats
						</h2>
						<div className="grid grid-cols-3 gap-6 text-center">
							{[
								{ label: 'Age', value: character.age || 'N/A' },
								{
									label: 'Height',
									value: character.height ? `${character.height} cm` : 'N/A',
								},
								{
									label: 'Weight',
									value: character.weight ? `${character.weight} kg` : 'N/A',
								},
							].map((stat, index) => (
								<div
									key={index}
									className="rounded-lg bg-card p-4 transition-all duration-300 hover:bg-accent"
								>
									<span className="text-sm font-medium text-primary">
										{stat.label}
									</span>
									<p className="text-2xl font-bold text-card-foreground">
										{stat.value}
									</p>
								</div>
							))}
						</div>
					</section>

					<Separator />

					<section className="rounded-md p-2 transition-all duration-300">
						<h2 className="mb-4 text-2xl font-semibold text-card-foreground">
							Traits
						</h2>
						<div className="grid gap-6 md:grid-cols-2">
							<div>
								<div className="flex flex-wrap gap-2">
									{character.positiveTraits
										?.split(',')
										.map((trait, index) => (
											<TraitBadge key={index} trait={trait} type="positive" />
										))}
								</div>
							</div>
							<div>
								<div className="flex flex-wrap gap-2">
									{character.negativeTraits
										?.split(',')
										.map((trait, index) => (
											<TraitBadge key={index} trait={trait} type="negative" />
										))}
								</div>
							</div>
						</div>
					</section>

					<Separator />

					<section className="rounded-md p-2 transition-all duration-300">
						<h2 className="mb-4 text-2xl font-semibold text-card-foreground">
							Lore
						</h2>
						<div className="rounded-lg bg-card p-4">
							{character.lore ? (
								<ScrollArea className="h-[250px] pr-4">
									{character.lore?.split('\n').map((paragraph, index) => (
										<p key={index} className="mb-4 text-muted-foreground">
											{paragraph}
										</p>
									))}
								</ScrollArea>
							) : (
								<p className="text-muted-foreground">No lore available</p>
							)}
						</div>
					</section>

					{galleryCount > 0 ? (
						<section
							className={cn(
								'h-[calc(100vh-80px)] rounded-md transition-all duration-300',
								galleryCount < 15 ? 'h-full' : '',
							)}
						>
							<h2 className="p-4 text-2xl font-semibold text-card-foreground">
								Gallery ({galleryCount})
							</h2>
							<SelectionProvider
								value={{
									handleSelection: () => {},
									selectedImagesIds: [],
									enableSelection: false,
									handleDialogImageClick: (image, type) =>
										openDialog(type, image),
								}}
							>
								<Masonry
									queryKey={character.slug}
									characterSlug={character.slug}
								/>
								{dialogState.image && (
									<>
										<DialogueImage
											open={dialogState.isImageOpen}
											onClose={() => closeDialog(WORKFLOWS.generateImage)}
											image={dialogState.image}
										/>
										<DialogueDetailer
											image={dialogState.image}
											open={dialogState.isDetailerOpen}
											onClose={() => closeDialog(WORKFLOWS.faceDetailer)}
											key={dialogState.image.id}
										/>
										<DialogUpscaler
											image={dialogState.image}
											open={dialogState.isUpscalerOpen}
											onClose={() => closeDialog(WORKFLOWS.upscaler)}
										/>
									</>
								)}
							</SelectionProvider>
						</section>
					) : (
						<></>
					)}
				</main>
			</div>
		</ScrollArea>
	)
}
